import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";

import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { CalendarGrid, DaySummary } from "./ProgramacaoCalendarParts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
export const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const ProgramacaoCalendar = ({ publicMode = false, primaryColor }) => {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [data, setData] = useState({ dias: {}, pode_criar: false });
  const [selected, setSelected] = useState(todayISO());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API}/calendar${publicMode ? "/public" : ""}`, { params: { mes: ym(cursor) } })
      .then(r => alive && setData(r.data))
      .catch(() => alive && setData({ dias: {}, pode_criar: false }))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [cursor, publicMode]);

  const dias = data.dias || {};
  const selectedItems = dias[selected] || [];
  const totalMes = useMemo(() => Object.values(dias).reduce((a, v) => a + v.length, 0), [dias]);

  const changeMonth = (delta) => {
    const d = new Date(cursor);
    d.setMonth(d.getMonth() + delta);
    setCursor(d);
  };

  return (
    <Card className="overflow-hidden" data-testid="programacao-calendar">
      <CardContent className="p-0">
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="p-2 rounded-xl bg-primary/10 shrink-0"><CalendarDays className="h-4 w-4 text-primary" /></div>
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-tight">Programação</p>
              <p className="text-[11px] text-muted-foreground">{totalMes} {totalMes === 1 ? "atividade" : "atividades"} no mês</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => changeMonth(-1)} data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="text-sm font-medium w-[112px] text-center" data-testid="calendar-month-label">{MESES[cursor.getMonth()]} {cursor.getFullYear()}</span>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => changeMonth(1)} data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>

        <CalendarGrid cursor={cursor} dias={dias} selected={selected} onSelect={setSelected} loading={loading} />

        <DaySummary
          selected={selected}
          items={selectedItems}
          podeCriar={!publicMode && data.pode_criar}
          podeEditar={!publicMode && data.pode_editar}
          onCreate={() => navigate(`/eventos?nova=${selected}`)}
          onEdit={(it) => navigate(`/eventos?editar=${it.id}`)}
          onOpenLink={(link) => link && (link.startsWith("http") ? window.open(link, "_blank") : navigate(link))}
        />
      </CardContent>
    </Card>
  );
};

export default ProgramacaoCalendar;
